import React from 'react';
import { useTranslation } from 'react-i18next';

import { CopyButton, Icon } from '@/nova/components';
import { COLORS } from '@/nova/theme/tokens';

import { LANDING_PATH_MAX } from '../../landing-page-api';
import * as S from './LandingPageComposer.styles';

/** Where the page stands: live, taken offline, failed to publish, or never published. */
export type BarStatus = 'live' | 'unpublished' | 'failed' | 'none';

export interface BrowserBarProps {
  /** What the path follows: "lp.<domain>/", or "/" while the landing domain is unknown. */
  host: string;
  /** The path as the owner edits it (no leading slash). */
  slug: string;
  onSlugChange: (value: string) => void;
  disabled: boolean;
  /** The path cannot be published at; `problemId` names the element that says why. */
  invalid: boolean;
  problemId?: string;
  /** The page's full address to copy; '' while there is none (an invalid path, or no landing domain). */
  url: string;
  onCopied?: () => void;
  onCopyFailed?: () => void;
  status: BarStatus;
}

/**
 * The preview's top edge, a quiet mock browser bar: three dots, the page's address in a rounded pill (a lock, the
 * landing host in a muted tone, the path as an inline field, and a copy button for the full address), and the page's
 * state as a dot and a word. A narrow bar drops the dots, then the word (the dot keeps it as its tooltip).
 */
export const BrowserBar = ({
  host,
  slug,
  onSlugChange,
  disabled,
  invalid,
  problemId,
  url,
  onCopied,
  onCopyFailed,
  status,
}: BrowserBarProps): React.ReactElement => {
  const { t } = useTranslation();
  const statusLabel =
    status === 'live'
      ? t('Live')
      : status === 'failed'
        ? t('Publishing failed')
        : status === 'unpublished'
          ? t('Unpublished')
          : t('Not published');

  return (
    <S.BrowserBar>
      <S.BarRow>
        <S.BarDots aria-hidden="true">
          <S.BarDot />
          <S.BarDot />
          <S.BarDot />
        </S.BarDots>
        <S.AddressPill $invalid={invalid} $disabled={disabled}>
          <Icon name="lock" size={12} color={COLORS.textFaint} />
          <S.AddressHost raw text={host} title={host} />
          <S.SlugInput
            value={slug}
            onChange={(e) => onSlugChange(e.target.value)}
            placeholder="welcome-bonus"
            maxLength={LANDING_PATH_MAX + 10}
            disabled={disabled}
            spellCheck={false}
            autoCapitalize="off"
            autoComplete="off"
            aria-label={t('Path')}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? problemId : undefined}
          />
          {url ? (
            <S.AddressCopy>
              <CopyButton
                value={url}
                label={t('Copy address')}
                size={13}
                onCopied={onCopied}
                onCopyFailed={onCopyFailed}
              />
            </S.AddressCopy>
          ) : null}
        </S.AddressPill>
        <S.BarStatus title={statusLabel}>
          <S.PubDot $tone={status === 'live' ? 'live' : status === 'failed' ? 'failed' : 'off'} aria-hidden="true" />
          <S.BarStatusText raw text={statusLabel} />
        </S.BarStatus>
      </S.BarRow>
    </S.BrowserBar>
  );
};

export default BrowserBar;
